import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '../components/Navbar';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import api from '../services/api';
import toast, { Toaster } from 'react-hot-toast';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { DotsVerticalIcon, Pencil1Icon, TrashIcon } from '@radix-ui/react-icons';
import './Global.css';

const TIPOS_LICENCA = [
  { value: 'SAUDE', label: 'Saúde' },
  { value: 'MATERNIDADE', label: 'Maternidade' },
  { value: 'PATERNIDADE', label: 'Paternidade' },
  { value: 'NOJO', label: 'Nojo' },
  { value: 'GALA', label: 'Gala' },
  { value: 'PREMIO', label: 'Prêmio' },
  { value: 'TRATO_INTERESSE', label: 'Trato de interesse particular' },
  { value: 'READAPTACAO_159', label: 'Readaptação (tipo 159) — NÃO afasta' },
  { value: 'OUTRO', label: 'Outro' },
];

const UserLicensesManagement = () => {
  const [licenses, setLicenses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [form, setForm] = useState({
    userId: '',
    tipo: 'SAUDE',
    dataInicio: '',
    dataFim: '',
    naoAfasta: false,
    observacao: '',
    isActive: true,
  });

  const load = async () => {
    setLoading(true);
    try {
      const [licRes, userRes] = await Promise.all([
        api.get('/user-licenses'),
        api.get('/users'),
      ]);
      setLicenses(Array.isArray(licRes.data) ? licRes.data : []);
      setUsers(Array.isArray(userRes.data) ? userRes.data : []);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar licenças');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  /**
   * Importa bm_licenca do SABM pra tabela local — é ela que a validação de
   * impedimento consulta na hora de lançar hora-aula.
   */
  const syncSabm = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/admin/legacy-sync/licenses');
      if (!data.legacyEnabled) {
        toast.error('Integração com o SABM desligada no backend.');
        return;
      }
      toast.success(
        `Licenças: ${data.criadas} importada(s), ${data.atualizadas} atualizada(s). ` +
          `Férias: ${data.feriasCriadas ?? 0} importada(s), ${data.feriasAtualizadas ?? 0} atualizada(s).`,
        { duration: 7000 },
      );
      if (data.semMilitarCorrespondente > 0) {
        toast(
          `${data.semMilitarCorrespondente} licença(s) sem militar correspondente no cadastro.`,
          { duration: 7000, icon: '⚠️' },
        );
      }
      load();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Erro ao sincronizar com o SABM');
    } finally {
      setSyncing(false);
    }
  };

  const resetForm = () => {
    setForm({
      userId: '',
      tipo: 'SAUDE',
      dataInicio: '',
      dataFim: '',
      naoAfasta: false,
      observacao: '',
      isActive: true,
    });
    setEditing(null);
  };

  const openCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const openEdit = (lic) => {
    setEditing(lic);
    setForm({
      userId: lic.userId,
      tipo: lic.tipo,
      dataInicio: lic.dataInicio?.slice(0, 10) || '',
      dataFim: lic.dataFim?.slice(0, 10) || '',
      naoAfasta: !!lic.naoAfasta,
      observacao: lic.observacao || '',
      isActive: lic.isActive,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.userId || !form.dataInicio || !form.dataFim) {
      toast.error('Preencha a pessoa, início e fim');
      return;
    }
    if (form.dataInicio > form.dataFim) {
      toast.error('Data início posterior à data fim');
      return;
    }
    try {
      if (editing) {
        await api.patch(`/user-licenses/${editing.id}`, {
          tipo: form.tipo,
          dataInicio: form.dataInicio,
          dataFim: form.dataFim,
          naoAfasta: form.naoAfasta,
          observacao: form.observacao || null,
          isActive: form.isActive,
        });
        toast.success('Licença atualizada');
      } else {
        await api.post('/user-licenses', form);
        toast.success('Licença cadastrada');
      }
      setShowModal(false);
      resetForm();
      load();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Erro ao salvar licença');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover esta licença?')) return;
    try {
      await api.delete(`/user-licenses/${id}`);
      toast.success('Removida');
      load();
    } catch (err) {
      toast.error('Erro ao remover');
    }
  };

  const filtered = useMemo(() => {
    const term = filter.trim().toLowerCase();
    if (!term) return licenses;
    return licenses.filter(
      (l) =>
        l.user?.name?.toLowerCase().includes(term) ||
        l.user?.matricula?.toLowerCase().includes(term) ||
        l.tipo?.toLowerCase().includes(term),
    );
  }, [licenses, filter]);

  const pagination = usePagination(filtered, 10);
  const { setPage } = pagination;

  useEffect(() => {
    setPage(1);
  }, [filter, setPage]);

  const labelTipo = (tipo) =>
    TIPOS_LICENCA.find((t) => t.value === tipo)?.label || tipo || '—';

  const formatDate = (value) =>
    value ? value.slice(0, 10).split('-').reverse().join('/') : '—';

  return (
    <>
      <Toaster position="top-right" />
      <Navbar />
      <div className="container">
        <div className="page-header-new">
          <div>
            <h1 style={{ margin: 0 }}>Licenças</h1>
            <p className="subtitle">
              Licença ou férias com cobertura ativa bloqueiam o lançamento de aula/monitoria —
              exceto se marcada "Não afasta" (readaptação tipo 159). Use "Sincronizar SABM" para
              importar de bm_licenca e bm_ferias; o que é importado não sobrescreve o que foi
              cadastrado à mão.
            </p>
          </div>
          <div className="header-actions">
            <button className="btn-secondary" onClick={syncSabm} disabled={syncing}>
              {syncing ? 'Sincronizando…' : 'Sincronizar SABM'}
            </button>
            <button className="btn-primary" onClick={openCreate}>
              + Nova Licença
            </button>
          </div>
        </div>

        <div className="table-controls">
          <div className="search-bar">
            <input
              type="text"
              className="search-input"
              placeholder="Buscar por nome, matrícula ou tipo..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Carregando licenças...</p>
          </div>
        ) : (
          <>
            <div className="table-wrapper">
              <table className="modern-table">
                <thead>
                  <tr>
                    <th>Pessoa</th>
                    <th>Matrícula</th>
                    <th>Tipo</th>
                    <th>Início</th>
                    <th>Fim</th>
                    <th className="text-center">Afasta?</th>
                    <th className="text-center">Origem</th>
                    <th className="text-center">Status</th>
                    <th className="text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {pagination.pageItems.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="text-center empty-state">
                        <strong>
                          {filter ? 'Nenhuma licença encontrada' : 'Nenhuma licença cadastrada'}
                        </strong>
                        {filter
                          ? 'Tente outro nome, matrícula ou tipo.'
                          : 'Cadastre à mão ou use "Sincronizar SABM" para importar.'}
                      </td>
                    </tr>
                  ) : (
                    pagination.pageItems.map((lic) => (
                      <tr key={lic.id}>
                        <td className="cell-primary">{lic.user?.name || '—'}</td>
                        <td>
                          <span className="matricula-text">{lic.user?.matricula || '—'}</span>
                        </td>
                        <td>{labelTipo(lic.tipo)}</td>
                        <td className="col-time">{formatDate(lic.dataInicio)}</td>
                        <td className="col-time">{formatDate(lic.dataFim)}</td>
                        <td className="text-center">
                          <span className={`badge ${lic.naoAfasta ? 'badge-user' : 'badge-warning'}`}>
                            {lic.naoAfasta ? 'Não afasta' : 'Afasta'}
                          </span>
                        </td>
                        <td className="text-center">
                          {lic.source === 'sabm' ? (
                            <span className="badge badge-info" title={`bm_licenca id=${lic.sourceId}`}>
                              SABM
                            </span>
                          ) : (
                            <span className="badge badge-user">Manual</span>
                          )}
                        </td>
                        <td className="text-center">
                          <span className={`badge ${lic.isActive ? 'badge-success' : 'badge-danger'}`}>
                            {lic.isActive ? 'Ativa' : 'Inativa'}
                          </span>
                        </td>
                        <td className="text-center cell-actions">
                          <DropdownMenu.Root>
                            <DropdownMenu.Trigger asChild>
                              <button className="icon-button" aria-label="Ações">
                                <DotsVerticalIcon />
                              </button>
                            </DropdownMenu.Trigger>
                            <DropdownMenu.Portal>
                              <DropdownMenu.Content className="dropdown-content" sideOffset={5}>
                                <DropdownMenu.Item
                                  className="dropdown-item"
                                  onSelect={() => openEdit(lic)}
                                >
                                  <Pencil1Icon />
                                  <span>Editar</span>
                                </DropdownMenu.Item>
                                <DropdownMenu.Separator className="dropdown-separator" />
                                <DropdownMenu.Item
                                  className="dropdown-item dropdown-item-danger"
                                  onSelect={() => handleDelete(lic.id)}
                                >
                                  <TrashIcon />
                                  <span>Remover</span>
                                </DropdownMenu.Item>
                              </DropdownMenu.Content>
                            </DropdownMenu.Portal>
                          </DropdownMenu.Root>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              id="licencas"
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              from={pagination.from}
              to={pagination.to}
              pageSize={pagination.pageSize}
              onPageChange={pagination.setPage}
              onPageSizeChange={pagination.setPageSize}
              itemLabel="licenças"
            />
          </>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editing ? 'Editar Licença' : 'Nova Licença'}</h2>
                <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
              </div>
              <form onSubmit={handleSubmit} className="form">
                <div className="form-group">
                  <label>Pessoa *</label>
                  <select
                    value={form.userId}
                    onChange={(e) => setForm({ ...form, userId: e.target.value })}
                    disabled={!!editing}
                    required
                  >
                    <option value="">Selecione…</option>
                    {users
                      // Militar é identificado por matrícula; civil, por CPF —
                      // e o validador de impedimento consulta licença dos dois.
                      .filter((u) => u.matricula || u.personnelType === 'civil')
                      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                      .map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name} ({u.matricula || (u.personnelType === 'civil' ? 'civil' : '—')})
                        </option>
                      ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Tipo *</label>
                  <select
                    value={form.tipo}
                    onChange={(e) => {
                      const tipo = e.target.value;
                      setForm({
                        ...form,
                        tipo,
                        // Readaptação 159 não afasta por padrão
                        naoAfasta: tipo === 'READAPTACAO_159' ? true : form.naoAfasta,
                      });
                    }}
                  >
                    {TIPOS_LICENCA.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Data início *</label>
                    <input
                      type="date"
                      value={form.dataInicio}
                      onChange={(e) => setForm({ ...form, dataInicio: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Data fim *</label>
                    <input
                      type="date"
                      value={form.dataFim}
                      onChange={(e) => setForm({ ...form, dataFim: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={form.naoAfasta}
                      onChange={(e) => setForm({ ...form, naoAfasta: e.target.checked })}
                    />
                    Não afasta (militar continua dando aula — ex: readaptação 159)
                  </label>
                </div>
                <div className="form-group">
                  <label>Observação</label>
                  <textarea
                    rows={2}
                    value={form.observacao}
                    onChange={(e) => setForm({ ...form, observacao: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    />
                    Ativa (desmarque para arquivar sem deletar)
                  </label>
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button type="button" onClick={() => setShowModal(false)}>Cancelar</button>
                  <button type="submit" className="btn-primary">
                    {editing ? 'Salvar' : 'Cadastrar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default UserLicensesManagement;
